/**
 * Nurse dashboard — today's schedule, assigned patients, pending reports.
 * Backend role-filters visits & patients to the calling nurse.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { visitAPI, patientAPI } from '../../../services/api';
import Skeleton from '../../../components/common/Skeleton';
import {
  T, FONT, I, firstNameOf, timeOfDay, asList, fmtDateTime,
  FadeSection, Hero, KpiCard, ChartCard, EmptyState, StatusBadge,
  Avatar, ListRow, QuickActions, PolarStyles,
} from '../shared';

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export default function NurseDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const firstName = firstNameOf(user);

  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [patients, setPatients] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      visitAPI.list({ limit: 100 }).catch(() => null),
      patientAPI.list({ limit: 50 }).catch(() => null),
    ]).then(([v, p]) => {
      if (v.status === 'fulfilled') setVisits(asList(v.value));
      if (p.status === 'fulfilled') setPatients(asList(p.value));
      setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 864e5);

  const todays = visits
    .filter(v => v.scheduled_start && sameDay(new Date(v.scheduled_start), now))
    .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
  const upcoming = visits.filter(v => {
    const st = String(v.status || '').toUpperCase();
    return !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(st) &&
      v.scheduled_start && new Date(v.scheduled_start) > now;
  });
  const completedThisWeek = visits.filter(v =>
    String(v.status || '').toUpperCase() === 'COMPLETED' &&
    v.scheduled_start && new Date(v.scheduled_start) >= weekAgo);

  // Visits that likely still need documentation/action.
  const needsAction = visits
    .filter(v => {
      const st = String(v.status || '').toUpperCase();
      if (['IN_PROGRESS', 'ARRIVED', 'EN_ROUTE'].includes(st)) return true;
      return st === 'COMPLETED' && !v.report_id && !v.has_report && !v.clinical_report_id;
    })
    .slice(0, 6);

  if (loading) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <Skeleton width="100%" height={120} style={{ marginBottom: 18, borderRadius: 20 }} />
      <Skeleton.KPIGrid count={4} cols={4} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Skeleton.Card rows={3} /><Skeleton.Card rows={3} />
      </div>
    </div>
  );

  const kpis = [
    { title: "Today's Visits", value: todays.length, color: T.primary, Icon: I.calendar, sub: todays.length ? 'on your schedule' : 'no visits today' },
    { title: 'Upcoming Visits', value: upcoming.length, color: T.info, Icon: I.clock, sub: 'scheduled ahead' },
    { title: 'Assigned Patients', value: patients.length, color: T.teal, Icon: I.heart, sub: 'under your care' },
    { title: 'Completed This Week', value: completedThisWeek.length, color: T.green, Icon: I.check, sub: 'visits done' },
  ];

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: T.text }}>
      <Hero
        index={0}
        roleLabel="Nurse workspace"
        title={`Good ${timeOfDay()}, Nurse ${firstName} 🩺`}
        subtitle="Your schedule, patients and visit documentation at a glance."
        stats={[
          { label: 'Today', value: todays.length, Icon: I.calendar },
          { label: 'My patients', value: patients.length, Icon: I.heart },
        ]}
        onRefresh={load}
      />

      <FadeSection index={1} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 18 }}>
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </FadeSection>

      <FadeSection index={2} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, marginBottom: 18 }}>
        <ChartCard title="Today's Schedule" subtitle="Visits scheduled for today, in order">
          {todays.length ? todays.map((v, i) => (
            <ListRow
              key={v.visit_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.primary}12` }}><I.calendar size={16} color={T.primary} /></span>}
              title={v.patient_name || `Visit #${v.visit_id ?? i + 1}`}
              sub={fmtDateTime(v.scheduled_start)}
              right={<StatusBadge value={v.status} />}
            />
          )) : (
            <EmptyState icon={I.calendar} color={T.primary} title="No visits scheduled today" caption="Enjoy the calm — newly dispatched visits will show up here." />
          )}
        </ChartCard>

        <ChartCard title="My Patients" subtitle="Patients assigned to your care">
          {patients.length ? patients.slice(0, 6).map((p, i) => (
            <div
              key={p.party_id || i}
              onClick={() => p.party_id && navigate(`/patient-360/${p.party_id}`)}
              role="link"
              title="Open Patient 360°"
              style={{ cursor: p.party_id ? 'pointer' : 'default' }}
            >
              <ListRow
                left={<Avatar name={p.name || p.full_name} index={i} />}
                title={p.name || p.full_name || `Patient #${p.party_id ?? i + 1}`}
                sub={p.phone || p.county || null}
                right={<StatusBadge value={p.status || 'ACTIVE'} />}
              />
            </div>
          )) : (
            <EmptyState icon={I.heart} color={T.teal} title="No assigned patients yet" caption="Patients will appear here once visits are dispatched to you." />
          )}
        </ChartCard>

        <ChartCard title="Needs Your Attention" subtitle="Active visits & completed visits awaiting a report">
          {needsAction.length ? needsAction.map((v, i) => (
            <ListRow
              key={v.visit_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.warning}12` }}><I.clipboard size={16} color={T.warning} /></span>}
              title={v.patient_name || `Visit #${v.visit_id ?? i + 1}`}
              sub={String(v.status || '').toUpperCase() === 'COMPLETED' ? 'Visit report may be outstanding' : fmtDateTime(v.scheduled_start)}
              right={<StatusBadge value={v.status} />}
            />
          )) : (
            <EmptyState icon={I.check} color={T.green} title="All caught up" caption="No visits currently need action or documentation." />
          )}
        </ChartCard>
      </FadeSection>

      <FadeSection index={3}>
        <QuickActions
          navigate={navigate}
          actions={[
            { label: 'My Visits',        path: '/visits',                        color: T.primary, Icon: I.calendar },
            { label: 'Clinical Reports', path: '/clinical',                      color: T.teal,    Icon: I.clipboard },
            { label: 'Patients',         path: '/ss/parties?role_code=PATIENT',  color: T.purple,  Icon: I.heart },
            { label: 'My Profile',       path: '/profile',                       color: T.info,    Icon: I.users },
          ]}
        />
      </FadeSection>

      <PolarStyles />
    </div>
  );
}
